"use strict";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
Object.defineProperty(exports, "__esModule", { value: true });
exports.getPresignedGetObjectUrl = exports.getObject = void 0;
const aws_client_utils_1 = require("@aws-amplify/core/internals/aws-client-utils");
const composers_1 = require("@aws-amplify/core/internals/aws-client-utils/composers");
const utils_1 = require("@aws-amplify/core/internals/utils");
const utils_2 = require("../utils");
const validateObjectUrl_1 = require("../../validateObjectUrl");
const base_1 = require("./base");
const USER_AGENT_HEADER = 'x-amz-user-agent';
const getObjectSerializer = async (input, endpoint) => {
    const url = new utils_1.AmplifyUrl(endpoint.url.toString());
    (0, utils_2.validateS3RequiredParameter)(!!input.Key, 'Key');
    url.pathname = (0, utils_2.serializePathnameObjectKey)(url, input.Key);
    url.search = new utils_1.AmplifyUrlSearchParams({
        'x-id': 'GetObject',
        ...(input.ResponseCacheControl && {
            'response-cache-control': input.ResponseCacheControl,
        }),
    }).toString();
    (0, validateObjectUrl_1.validateObjectUrl)({
        bucketName: input.Bucket,
        key: input.Key,
        objectURL: url,
    });
    return {
        method: 'GET',
        headers: {
            ...(input.Range && { Range: input.Range }),
            ...(0, utils_2.assignStringVariables)({
                'x-amz-expected-bucket-owner': input.ExpectedBucketOwner,
            }),
        },
        url,
    };
};
const getObjectDeserializer = async (response) => {
    if (response.statusCode >= 300) {
        // error is always set when statusCode >= 300
        throw (0, utils_2.buildStorageServiceError)((await (0, base_1.parseXmlError)(response)));
    }
    else {
        return {
            ...(0, utils_2.map)(response.headers, {
                DeleteMarker: ['x-amz-delete-marker', utils_2.deserializeBoolean],
                AcceptRanges: 'accept-ranges',
                Expiration: 'x-amz-expiration',
                Restore: 'x-amz-restore',
                LastModified: ['last-modified', utils_2.deserializeTimestamp],
                ContentLength: ['content-length', utils_2.deserializeNumber],
                ETag: 'etag',
                ChecksumCRC32: 'x-amz-checksum-crc32',
                ChecksumCRC32C: 'x-amz-checksum-crc32c',
                ChecksumSHA1: 'x-amz-checksum-sha1',
                ChecksumSHA256: 'x-amz-checksum-sha256',
                ChecksumType: 'x-amz-checksum-type',
                MissingMeta: ['x-amz-missing-meta', utils_2.deserializeNumber],
                VersionId: 'x-amz-version-id',
                CacheControl: 'cache-control',
                ContentDisposition: 'content-disposition',
                ContentEncoding: 'content-encoding',
                ContentLanguage: 'content-language',
                ContentRange: 'content-range',
                ContentType: 'content-type',
                Expires: ['expires', utils_2.deserializeTimestamp],
                WebsiteRedirectLocation: 'x-amz-website-redirect-location',
                ServerSideEncryption: 'x-amz-server-side-encryption',
                SSECustomerAlgorithm: 'x-amz-server-side-encryption-customer-algorithm',
                SSECustomerKeyMD5: 'x-amz-server-side-encryption-customer-key-md5',
                SSEKMSKeyId: 'x-amz-server-side-encryption-aws-kms-key-id',
                BucketKeyEnabled: [
                    'x-amz-server-side-encryption-bucket-key-enabled',
                    utils_2.deserializeBoolean,
                ],
                StorageClass: 'x-amz-storage-class',
                RequestCharged: 'x-amz-request-charged',
                ReplicationStatus: 'x-amz-replication-status',
                PartsCount: ['x-amz-mp-parts-count', utils_2.deserializeNumber],
                TagCount: ['x-amz-tagging-count', utils_2.deserializeNumber],
                ObjectLockMode: 'x-amz-object-lock-mode',
                ObjectLockRetainUntilDate: [
                    'x-amz-object-lock-retain-until-date',
                    utils_2.deserializeTimestamp,
                ],
                ObjectLockLegalHoldStatus: 'x-amz-object-lock-legal-hold',
            }),
            Metadata: (0, utils_2.deserializeMetadata)(response.headers),
            $metadata: (0, aws_client_utils_1.parseMetadata)(response),
            // @ts-expect-error The body is a CompatibleHttpResponse type because the lower-level handler is XHR instead of
            // fetch, which represents payload in Blob instread of ReadableStream.
            Body: response.body,
        };
    }
};
exports.getObject = (0, composers_1.composeServiceApi)(utils_2.s3TransferHandler, getObjectSerializer, getObjectDeserializer, { ...base_1.defaultConfig, responseType: 'blob' });
/**
 * Get a presigned URL for the `getObject` API.
 *
 * @internal
 */
const getPresignedGetObjectUrl = async (config, input) => {
    const endpoint = base_1.defaultConfig.endpointResolver(config, input);
    const { url, headers, method } = await getObjectSerializer(input, endpoint);
    if (config.userAgentValue) {
        url.searchParams.append(config.userAgentHeader ?? USER_AGENT_HEADER, config.userAgentValue);
    }
    if (input.ResponseContentType) {
        url.searchParams.append('response-content-type', input.ResponseContentType);
    }
    if (input.ResponseContentDisposition) {
        url.searchParams.append('response-content-disposition', input.ResponseContentDisposition);
    }
    for (const [headerName, value] of Object.entries(headers).sort(([key1], [key2]) => key1.localeCompare(key2))) {
        url.searchParams.append(headerName, value);
    }
    return (0, aws_client_utils_1.presignUrl)({ method, url, body: aws_client_utils_1.UNSIGNED_PAYLOAD }, {
        signingService: base_1.defaultConfig.service,
        signingRegion: config.region,
        ...base_1.defaultConfig,
        ...config,
    });
};
exports.getPresignedGetObjectUrl = getPresignedGetObjectUrl;
